import React, {useContext, useEffect, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';
import CommentInputArea from 'components/pictures/detail/CommentInputArea';
import PictureComment from 'components/pictures/detail/PictureComment';
import {commentPictureRequest} from 'util/api';
import {CommentContext} from 'util/CommentContext';
import {UserContext} from 'components/user/UserContext';

const PictureCommentsPane = ({pictureId, isImageMy}) => {
    const [highlightedComment, setHighlightedComment] = useState(null);
    const [parentOfHighlightId, setParentOfHighlightId] = useState(null);
    const {setImageId, comments, totalComments} = useContext(CommentContext);

    const commentInputFieldRef = useRef(null);

    const [visibleComments, setVisibleComments] = useState(null); // Initialize as null for loading state

    const {user} = useContext(UserContext);
    const location = useLocation();

    const previousPictureIdRef = useRef(null);
    const previousLocationHashRef = useRef(null);

    useEffect(() => {

        if (!comments) {
            setVisibleComments(null);
            return;
        }
        const openedComments = comments.filter((filterComment) => filterComment.order || filterComment.order === 0);
        setVisibleComments(openedComments); // Set visible comments after loading
    }, [comments]);

    useEffect(() => {
        if (pictureId) {
            setImageId(pictureId);
        }
        previousPictureIdRef.current = pictureId;
    }, [pictureId]);

    useEffect(() => {
        const slowlySetHighlightedComment = (hash) => {
            setTimeout(() => {
                setHighlightedComment(hash);
            }, 20);
        };

        if (!comments || comments.length === 0 || previousLocationHashRef.current === location.hash) {
            return;
        }
        previousLocationHashRef.current = location.hash;

        const hash = location.hash.substring(1);
        if (!hash) {
            return;
        }
        slowlySetHighlightedComment(hash);

        const isResponse = !comments.some((comment) => comment.id === hash);
        if (!isResponse) {
            const element = document.getElementById('comment_' + hash);
            if (element) {
                element.scrollIntoView({behavior: 'smooth'});
            } else {
                const commentToAdd = comments.find((comment) => comment.id === hash);
                if (commentToAdd) {
                    if (!commentToAdd.order && commentToAdd.order !== 0) {
                        setVisibleComments((prevVisibleComments) => {
                            if (!prevVisibleComments.some((comment) => comment.id === hash)) {
                                commentToAdd.order = 0;
                                return [...prevVisibleComments, commentToAdd];
                            }
                            return prevVisibleComments;
                        });
                    }
                    const scrollToElement = () => {
                        const newElement = document.getElementById('comment_' + hash);
                        if (newElement) {
                            newElement.scrollIntoView({behavior: 'smooth'});
                        }
                    };
                    setTimeout(scrollToElement, 100);
                }
            }
        } else {
            const beholdingComment = comments.find(comment =>
                comment.commentResponses.some(response => response.id === hash)
            );
            if (!beholdingComment) {
                return;
            }
            if (!beholdingComment.order && beholdingComment.order !== 0) {
                setVisibleComments((prevVisibleComments) => {
                    if (!prevVisibleComments.some((comment) => comment.id === hash)) {
                        beholdingComment.order = 0;
                        return [...prevVisibleComments, beholdingComment];
                    }
                    return prevVisibleComments;
                });
            }
            setParentOfHighlightId(beholdingComment.id);
        }
    }, [location.hash, comments]);

    useEffect(() => {
        const handleClick = () => {
            if (location.hash) {
                setHighlightedComment(null);
            }
        };

        document.addEventListener('click', handleClick);

        return () => {
            document.removeEventListener('click', handleClick);
        };
    }, [location]);

    const postChatMessage = (input) => {
        const {inputValue, onSuccess, onError} = input;

        commentPictureRequest(pictureId, inputValue, user.balance)
            .then(() => {
                onSuccess?.();
            })
            .catch((error) => {
                onError?.(error);
            });
    };

    const handleReactionChange = (commentId, emoji) => {
        console.log('Reaction changed for comment: ', commentId, ' | ', emoji);
    };

    const showMoreComments = () => {
        if (!visibleComments) return; // Prevent interaction when loading
        const currentCount = visibleComments.length;
        const newVisibleCount = currentCount + 10;
        const newVisibleComments = comments.slice(0, newVisibleCount);

        newVisibleComments.forEach((comment, index) => {
            if (comment.order == null) {
                comment.order = index + currentCount + 1;
            }
        });

        setVisibleComments(newVisibleComments);
    };

    return (
        <div className="picture-comments-pane">
            <h3 style={{marginBottom: '0', marginTop: '8px'}}>Comments{totalComments ? ` (${totalComments})` : ''}</h3>
            {user.isLoggedIn ? (
                <CommentInputArea
                    ref={commentInputFieldRef}
                    isImageMy={isImageMy}
                    postChatMessage={postChatMessage}
                    inputPlaceholder="Write a comment..."
                />
            ) : (
                <div style={{height: '5px'}}/>
            )}

            <div className="comments-section">
                {visibleComments === null &&
                    <p>Loading comments...</p>
                }
                {visibleComments && visibleComments.length === 0 && (
                    <p>No comments yet.</p>
                )}
                {visibleComments && visibleComments.length > 0 && (
                    visibleComments.map((comment) => (
                        <div key={comment.id} id={`comment_${comment.id}`}>
                            <PictureComment
                                pictureId={pictureId}
                                comment={comment}
                                isHighlighted={highlightedComment === comment.id}
                                onReactionChange={handleReactionChange}
                                allowPin={isImageMy}
                                highlightedResponseId={parentOfHighlightId === comment.id ? highlightedComment : null}
                            />
                        </div>
                    )))
                }
                {visibleComments && comments && comments.length > visibleComments.length && (
                    <button
                        className="comment-show-replies-div"
                        onClick={showMoreComments}
                        disabled={false} // Disable button while loading
                    >
                        Show {comments.length - visibleComments.length} more comments
                    </button>
                )}
            </div>
        </div>
    );
};

export default PictureCommentsPane;
